import React, { FC } from 'react';
import { useTheme } from '@mui/material';

import { IAbsenceBalanceDTO } from 'src/api/dtos';
import { HorizontalBarChart } from 'src/shared/components';
import { DurationFormatEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDuration, mapHorizontalBarDarkColors, mapHorizontalBarLightColors } from 'src/shared/utils';

import { AccrualBalancesMetricHeader } from './AccrualBalancesMetricHeader';

interface Props {
  balance: IAbsenceBalanceDTO | undefined;
  shouldDisplayAsDays: boolean;
}

export const AccrualBalancesMetrics: FC<Props> = ({ balance, shouldDisplayAsDays }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const mapper = theme.palette.mode === 'light' ? mapHorizontalBarLightColors : mapHorizontalBarDarkColors;

  const total = getTotal(balance);

  const usedBalanceHours = formatDuration(balance?.used?.amountInSeconds, DurationFormatEnums.T_24_HOURS, 'seconds');
  const todayBalanceHours = formatDuration(balance?.today?.amountInSeconds, DurationFormatEnums.T_24_HOURS, 'seconds');

  const usedBalanceDays = balance?.used?.amountInDays && shouldDisplayAsDays ? balance.used.amountInDays : null;
  const todayBalanceDays = balance?.today?.amountInDays && shouldDisplayAsDays ? balance.today.amountInDays : null;

  const formattedUsedValue = usedBalanceDays
    ? `${usedBalanceHours} / ${usedBalanceDays} ${t('global.days')}`
    : usedBalanceHours;

  const formattedTodayValue = todayBalanceDays
    ? `${todayBalanceHours} / ${todayBalanceDays} ${t('global.days')}`
    : todayBalanceHours;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-around m-4 pb-2">
        <AccrualBalancesMetricHeader
          title={t('absences.usedToDate')}
          value={formattedUsedValue}
          tooltipMessage={t('absences.howMuchTimeHaveYouTakenUpToToday')}
        />
        <AccrualBalancesMetricHeader
          title={t('absences.todaysBalance')}
          value={formattedTodayValue}
          tooltipMessage={t('absences.totalBalanceLeftAsOfToday')}
        />
      </div>
      <div>
        <HorizontalBarChart
          color={mapper[0]}
          variant={balance?.used?.amountInSeconds ? 'dark' : 'light'}
          label={t('absences.usedToDate')}
          extraLabel={balance?.used?.amountInSeconds ? formattedUsedValue : undefined}
          value={balance?.used?.amountInSeconds ? getValue(+balance.used.amountInSeconds, total) : 0}
        />
        <HorizontalBarChart
          color={mapper[1]}
          variant={balance?.today?.amountInSeconds ? 'dark' : 'light'}
          label={t('absences.todaysBalance')}
          extraLabel={balance?.today?.amountInSeconds ? formattedTodayValue : undefined}
          value={balance?.today?.amountInSeconds ? getValue(+balance.today.amountInSeconds, total) : 0}
        />
      </div>
    </div>
  );
};

const getTotal = (balance: IAbsenceBalanceDTO | undefined): number => {
  if (!balance) {
    return 0;
  }

  const usedAmountInSeconds = balance.used?.amountInSeconds ? balance.used.amountInSeconds : 0;
  const todayAmountInSeconds = balance.today?.amountInSeconds ? balance.today.amountInSeconds : 0;

  return Math.abs(+usedAmountInSeconds) + Math.abs(+todayAmountInSeconds);
};

const getValue = (duration: number, total: number): number => (duration / total) * 100;
